// Copyright (c) 2023 WiseTime. All rights reserved.

import { GetTagCategoryRequest } from "../../../generated/legebill/site/tag/v4/tag_pb"
import { EMPTY, Observable, of } from "rxjs"
import { TagCategory } from "../../../generated/legebill/type/tag_category_pb"
import { Action } from "redux"
import { catchError, filter, finalize, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TagsSlice from "../slice"
import { grpc } from "../../../app/rpc/executor"
import { tagServiceClient } from "../../../app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import Grpc from "grpc-web"
import { showSnackBar } from "../../snackbar/actions"
import { t } from "@lingui/macro"
import { RootState } from "../../../app/store"

type GetTagCategoryRpc = (request: GetTagCategoryRequest) => Observable<TagCategory>

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Set<string>()

// Export for testing
export const __test__ = { inFlightRequests }

export const getTagCategory = (getTagCategoryRpc: GetTagCategoryRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getTagCategory.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: request }, state]) => {
        const tagCategoryId = request.getTagCategoryId()
        // Skip if tag category already in cache or request is already in flight
        if (state.tags.tagCategoriesCache?.[tagCategoryId] || inFlightRequests.has(tagCategoryId)) {
          return EMPTY
        }

        // Mark as in flight
        inFlightRequests.add(tagCategoryId)

        return of(request)
          .pipe(
            mergeMap(getTagCategoryRpc),
            map(t => [t.toObject()]),
            map(TagsSlice.setTagCategories),
            catchError(handleError),
            // Clean up in-flight tracking on completion or error
            finalize(() => inFlightRequests.delete(tagCategoryId))
          )
      })
    )

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === Grpc.StatusCode.NOT_FOUND) {
      return of(showSnackBar({
        message: t({ id: "msg_get_tag_category_not_found", message: "Tag category not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
        messageType: "ERROR"
      }))
    }

    if (code === Grpc.StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_get_tag_category_permission_denied", message: "Failed to load tag category, permission denied." }),
        messageType: "ERROR"
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_get_tag_category_failed", message: "Failed to load tag category. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const getTagCategoryRpc = (request: GetTagCategoryRequest): Observable<TagCategory> =>
  grpc(() => tagServiceClient.getTagCategory(request, null))

export default getTagCategory(getTagCategoryRpc)
